import { Upload } from '@mui/icons-material';
import {
  Alert,
  Button,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { createRef, useEffect, useState } from 'react';
import { useKundmarkeContext } from '../common/contexts/KundmarkeContext';
import { ApiError } from '../types/ApiError';
import { ApiResponse } from '../types/ApiResponse';
import { FormError } from '../types/FormError';
import { LoadState } from '../types/LoadState';
import * as Client from './ForteckningarClient';
import { ForteckningstransaktionSelector } from './ForteckningstransaktionSelector';
import { ValdaFastigheter } from './ValdaFastigheter';
import { UploadResponseDto } from './types/dtos/UploadResponseDto';

const tooManyFastigheter =
  'Förteckningar med fler än 500 fastigheter produceras inte. Kontakta kundservice för dialog.';

const allowedExtensions = [
  'csv',
  'txt',
  'xlsx',
  'xls',
  'zip',
  'json',
  'geojson',
];

const getUploadError = (response: ApiResponse<ApiError>): FormError => {
  const message = response?.data?.message;
  if (response.statusCode === 400) {
    if (message?.includes('Polygon cannot be self intersecting')) {
      return {
        text: 'Shapefilens polygon får inte vara självkorsande.',
        severity: 'error',
      };
    }
    if (message?.includes('but must contain exactly 1 geometry')) {
      return {
        text: 'Shapefilen innehåller flera polygoner, men får endast innehålla en.',
        severity: 'error',
      };
    }
    if (message?.includes('Area is too large')) {
      const area = message.match(/([0-9]+)$/)?.[1];
      return {
        text: area
          ? `Shapefilens yta är ${Math.round(
              Number.parseInt(area) / 1000000
            )} km2, vilket är för stort. Maxarea är 30 km2.`
          : 'Shapefilens yta är för stor. Maxarea är 30 km2.',
        severity: 'error',
      };
    }
  }
  return { text: 'Okänt fel.', severity: 'error' };
};

export interface AngeFastigheterProps {
  skapaForteckning: (selectedTransaktionId: string) => void;
  selectedTransaktionId: string;
  setSelectedTransaktionId: (value: string) => void;
  registerbeteckningar: string[];
  setRegisterbeteckningar: (value: string[]) => void;
  setTmpRegisterbeteckningar: (value: string[]) => void;
  registerbeteckningarPageState: [
    number,
    React.Dispatch<React.SetStateAction<number>>,
  ];
}

export const AngeFastigheter: React.FC<AngeFastigheterProps> = ({
  skapaForteckning,
  selectedTransaktionId,
  setSelectedTransaktionId,
  registerbeteckningar,
  setRegisterbeteckningar,
  setTmpRegisterbeteckningar,
  registerbeteckningarPageState,
}) => {
  const { kundmarke, setKundmarke, visaKundmarke } = useKundmarkeContext();
  const [loadState, setLoadState] = useState(LoadState.Idle);
  const [error, setError] = useState<FormError>();

  const fileUploadRef = createRef<HTMLInputElement>();

  useEffect(() => {
    if (registerbeteckningar.length > 500) {
      setError({ text: tooManyFastigheter, severity: 'error' });
    }
  }, [registerbeteckningar]);

  const selectFile = () => fileUploadRef.current?.click();

  const resetOnInputClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>
  ) => (event.currentTarget.value = '');

  const onFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files !== null && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!allowedExtensions.includes(file.name.split('.').pop())) {
        return setError({
          text: `Filen måste vara av en av typerna ${allowedExtensions
            .map((ext) => `.${ext}`)
            .join(', ')}.`,
          severity: 'error',
        });
      }

      setLoadState(LoadState.Loading);
      setError(undefined);
      const response = await Client.uploadShpFile(file);
      if (!response.success) {
        setError(getUploadError(response as ApiResponse<ApiError>));
        return setLoadState(LoadState.Error);
      }
      setLoadState(LoadState.Success);
      const data = response.data as UploadResponseDto;
      if (data.valid?.length) {
        setTmpRegisterbeteckningar(data.valid);
      }

      if (data.invalid?.length) {
        setError(
          data.valid?.length
            ? {
                text: (
                  <>
                    Filen kan hanteras men innehåller felaktiga rader. Följande
                    rader är felaktiga:
                    {data.invalid.map((invalidEntry) => (
                      <>
                        <br />
                        {invalidEntry}
                      </>
                    ))}
                  </>
                ),
                severity: 'warning',
              }
            : {
                text: 'Alla beteckningar i filen är ogiltiga.',
                severity: 'error',
              }
        );
      } else {
        setError(undefined);
      }
      setLoadState(LoadState.Idle);
    }
  };

  return (
    <Stack
      spacing={3}
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{ pt: 3 }}
    >
      <Typography variant="body2">
        Ladda upp en fil med de fastigheter som ska ingå i förteckningen. Filen
        kan vara en komma- eller semikolonseparerad CSV-fil, Excel-fil,
        geojson-fil eller Shape-fil med en polygon. Mer information om filformat
        hittar du{' '}
        <Link href="https://metria.se/metria-fastighets%C3%B6k-support-fastighets%C3%B6k-webb-f%C3%B6rteckningar-i-fastighets%C3%B6k-webb">
          här
        </Link>
        .
      </Typography>
      {error && <Alert severity={error.severity}>{error.text}</Alert>}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ width: '100%' }}
      >
        <Button onClick={selectFile} endIcon={<Upload />} variant="outlined">
          Läs in fastigheter från fil
        </Button>
        <input
          style={{ display: 'none' }}
          ref={fileUploadRef}
          type="file"
          onChange={onFileInputChange}
          onClick={resetOnInputClick}
        />
      </Stack>
      <ValdaFastigheter
        loadState={loadState}
        registerbeteckningar={registerbeteckningar}
        setRegisterbeteckningar={setRegisterbeteckningar}
        pageState={registerbeteckningarPageState}
      />
      <ForteckningstransaktionSelector
        selectedTransaktionId={selectedTransaktionId}
        setSelectedTransaktionId={setSelectedTransaktionId}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          skapaForteckning(selectedTransaktionId);
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={2}
          sx={{ mt: 2, mb: 2 }}
        >
          {visaKundmarke ? (
            <TextField
              label="Kundmärke"
              type="text"
              value={kundmarke}
              size="small"
              onChange={(e) => setKundmarke(e.currentTarget.value)}
            />
          ) : null}
          <Button
            variant="contained"
            type="submit"
            disabled={
              registerbeteckningar.length === 0 ||
              registerbeteckningar.length > 500
            }
          >
            Skapa förteckning
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};
